import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { SelectModule } from 'primeng/select';
import { Aggregation, Aggregations, Filter } from '../../filters';
import { AbstractFilterComponent } from '../abstract-filter.component';
import { updateSelectionCache, writePersistentSelection } from '../filter-groups.helper';
import { FilterOptionComponent } from '../filter-option/filter-option.component';
import { CompoundFilterConfig } from '../../../compound-filter-selection/compound-filter-selection.model';

@Component({
  selector: 'cbre-equal-filter',
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatDatepickerModule,
    SelectModule,
    MatMomentDateModule,
    FilterOptionComponent,
  ],
  templateUrl: './equal.component.html',
  styleUrls: ['./equal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EqualFilterComponent extends AbstractFilterComponent {
  private readonly _cdr = inject(ChangeDetectorRef);

  compoundFilterConfig = signal<CompoundFilterConfig | null>(null);
  protected readonly _clearVisible = signal(false);

  showOptionMenuIcon = computed(() => !this.filter$()?.hideOptionMenuIcon);

  @Output() filterOptionChange = new EventEmitter<Filter>();

  @Input() set aggregations(value: Aggregations) {
    this._aggregations = value ?? {};
    if (this.filter$()?.inputType === 'dropdown') this._setUpFilterOptions();
  }

  protected _aggregations: Aggregations;

  /**
   * Cache of Aggregation objects keyed by their option value.
   * Preserves displayName for the selected value even when absent from current aggregations.
   */
  private _selectedOptionCache = signal<Map<string | number, Aggregation>>(new Map());

  protected _setUpFilter() {
    if (this.filter$() == null || this._form == null) return;
    const key = this.filter$()?.key?.toString();
    if (!key) return;

    this.control = this._form.get(key) as FormControl;
    this._clearVisible.set(!!this.control?.value);

    this._destroy$.next(true);
    this.control.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(value => {
      this._checkValuePresence();
      this._clearVisible.set(!!value);
      this._cdr.markForCheck();
      this._syncFilterValue(value);
    });
  }

  protected _setUpFilterOptions() {
    const filter = this.filter$();
    const aggregation = this._aggregations[filter?.key] ?? [];
    const activeAggregations = aggregation.filter(a => a?.key != null && (a.displayName ?? '') !== '');

    // Update cache with current aggregation options that match the selected value
    this._updateSelectedOptionCache(activeAggregations);

    // Build active options
    const activeOptions = activeAggregations.map(option => ({
      label: this._makeOptionLabel(filter, option),
      value: this._makeOptionValue(option),
    }));

    // If the currently selected value is missing from aggregations, append it as a disabled-label option
    const selectedValue = this.control?.value;
    const missingOption = this._getMissingSelectedOption(activeAggregations, selectedValue);

    if (missingOption) {
      const disabledLabel = this._makeDisabledOptionLabel(filter, missingOption);
      this.options = [
        ...activeOptions,
        { label: disabledLabel, value: this._makeOptionValue(missingOption), disabledLabel: true },
      ];
    } else {
      this.options = activeOptions;
    }

    // Persist selection metadata
    this._updatePersistentSelection();
    this._checkValuePresence();
  }

  public handleChange(): void {
    const filter = this.filter$();
    const aggregation = (this._aggregations[filter?.key] ?? []).filter(
      a => a?.key != null && (a.displayName ?? '') !== ''
    );
    this._updateSelectedOptionCache(aggregation);
    this._updatePersistentSelection();
    this._checkValuePresence();
    this._clearVisible.set(!!this.control?.value);
    this._syncFilterValue(this.control?.value);
  }

  /**
   * Updates filter$ with the latest control value and keeps active compoundFilters in sync.
   * Compound filters that were active (values != null) are updated to the new value;
   * inactive ones (values == null) remain null.
   */
  private _syncFilterValue(value: any): void {
    this.filter$.update(f => {
      if (!f) return f;
      const compoundFilters = f.compoundFilters?.length
        ? f.compoundFilters.map(cf => ({ ...cf, values: (cf as any).values != null ? value : null }))
        : f.compoundFilters;
      return { ...f, values: value, compoundFilters };
    });
  }

  private _updateSelectedOptionCache(activeAggregations: Aggregation[]): void {
    const selectedValue = this.control?.value;
    if (selectedValue == null) return;

    const cache = new Map(this._selectedOptionCache());
    updateSelectionCache(cache, selectedValue, activeAggregations, this.filter$(), this._makeOptionValue);
    this._selectedOptionCache.set(cache);
  }

  private _getMissingSelectedOption(activeAggregations: Aggregation[], selectedValue: any): Aggregation | null {
    if (selectedValue == null) return null;
    const inAgg = activeAggregations.some(o => this._makeOptionValue(o) === selectedValue);
    if (inAgg) return null;

    const cached = this._selectedOptionCache().get(selectedValue);
    return cached ?? { key: String(selectedValue), value: 0, displayName: String(selectedValue) };
  }

  private _updatePersistentSelection(): void {
    writePersistentSelection(this.filter$(), this.control?.value, this._selectedOptionCache());
  }

  protected _makeOptionLabel = ({ dataType }: Pick<Filter, 'dataType'>, { key, value, displayName }: Aggregation) => {
    const count = value == null || isNaN(Number(value)) ? '' : ` (${Number(value)})`;
    const label =
      dataType === 'boolean' && !displayName ? `${this._keyValue(key)}${count}` : `${displayName || key}${count}`;

    return label.toUpperCase();
  };

  protected _makeDisabledOptionLabel = (
    { dataType }: Pick<Filter, 'dataType'>,
    { key, displayName }: Aggregation
  ): string => {
    const label = dataType === 'boolean' && !displayName ? `${this._keyValue(key)} (0)` : `${displayName || key} (0)`;
    return label.toUpperCase();
  };

  protected _keyValue = key => {
    if (key === '1') return 'Y';
    return key === '0' ? 'N' : key;
  };

  protected _makeOptionValue = ({ key }: Aggregation) => key;
}
